import React, { useMemo } from 'react';
import { RadialBarChart, RadialBar, PolarAngleAxis } from 'recharts';
import { Box, Typography, Stack, Grid, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { amber } from '@mui/material/colors';

const ComplianceScoreDonutChart = ({ data, loading }) => {
  const theme = useTheme();

  // Use MUI breakpoints for responsive sizing
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.only('md'));

  // Calculate chart size based on breakpoints - made bigger
  const chartSize = useMemo(() => {
    if (isXs) return 280;
    if (isSm) return 320;
    if (isMd) return 360;
    return 400;
  }, [isXs, isSm, isMd]);

  const calculateCompliance = () => {
    if (!data || data.total === 0) return 0;
    const approved = data.approved || 0;
    const total = data.total || 0;
    return ((approved / total) * 100).toFixed(2);
  };

  const complianceScore = calculateCompliance();

  // Return color based on compliance percentage
  const getComplianceColor = () => {
    if (complianceScore >= 80) return theme.palette.success.main;
    if (complianceScore >= 60) return amber[500];
    if (complianceScore >= 40) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const complianceColor = getComplianceColor();

  const chartData = [
    {
      name: 'Compliance',
      value: complianceScore,
      fill: complianceColor,
    },
  ];

  const center = chartSize / 2;
  const innerRadius = chartSize * 0.23;
  const outerRadius = chartSize * 0.33;

  return (
    <Box
      sx={{
        p: 0.5,
        bgcolor: theme.palette.background.paper,
        borderRadius: 2,
        boxShadow: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        outline: 'none !important',
        border: 'none',
        '&:focus': {
          outline: 'none !important',
        },
        '&:focus-visible': {
          outline: 'none !important',
        },
      }}
    >
      <Typography variant="h6" sx={{ m: 1, fontWeight: 600 }}>
        {'Compliance Score'}
      </Typography>

      <Stack
        direction="column"
        alignItems="center"
        spacing={0}
        sx={{ flex: 1, justifyContent: 'flex-start', width: '100%', minHeight: 0 }}
      >
        {/* Radial Chart */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 0,
            py: 0,
            '& svg': {
              maxWidth: { xs: '280px', sm: '320px', md: '360px', lg: '400px' },
              width: '100%',
              height: 'auto',
              outline: 'none !important',
              border: 'none !important',
              '&:focus': {
                outline: 'none !important',
                border: 'none !important',
              },
              '&:focus-visible': {
                outline: 'none !important',
                border: 'none !important',
              },
              '& *': {
                outline: 'none !important',
                border: 'none !important',
                '&:focus': {
                  outline: 'none !important',
                  border: 'none !important',
                },
                '&:focus-visible': {
                  outline: 'none !important',
                  border: 'none !important',
                },
              },
            },
            '&:focus': {
              outline: 'none !important',
              border: 'none !important',
            },
            '&:focus-visible': {
              outline: 'none !important',
              border: 'none !important',
            },
            '& *': {
              outline: 'none !important',
              border: 'none !important',
              '&:focus': {
                outline: 'none !important',
                border: 'none !important',
              },
            },
          }}
        >
          <RadialBarChart
            width={chartSize}
            height={chartSize}
            cx={center}
            cy={center}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            barSize={chartSize * 0.038}
            data={chartData}
            startAngle={90}
            endAngle={-270}
          >
            <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
            <RadialBar
              background={{ fill: theme.palette.grey[300] }}
              dataKey="value"
              cornerRadius={6}
              isAnimationActive={true}
              animationDuration={800}
            />
          </RadialBarChart>
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, right: 0 }}
          >
            <Typography variant="h4" fontWeight="bold">
              {loading ? '...' : `${complianceScore}%`}
            </Typography>
            <Typography variant="body2" color={theme.palette.grey[500]} fontWeight={500}>
              {'Compliance'}
            </Typography>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

export default ComplianceScoreDonutChart;
